import axios from 'axios';
import { IVFSResponse } from '../../services/fileSystemService';
import { apiV2 } from '../../lib/baseAxios';
import logger from '../../lib/logger';
import {
  InitializeLiveUploadRequest,
  InitializeLiveUploadResponse,
  CreateSessionRequest,
  SessionResponse,
  InitUploadRequest,
  InitUploadResponse,
  GetUploadUrlRequest,
  GetUploadUrlResponse,
  FinalizeUploadRequest,
  FinalizeUploadResponse,
  TranscriptUpdateRequest,
  NotesUpdateRequest,
  FinalizeSessionRequest,
} from './types';

export const initializeLiveUpload = async (
  teamId: string,
  options: InitializeLiveUploadRequest,
) => {
  const response = await apiV2.post<IVFSResponse<InitializeLiveUploadResponse>>(
    `/live/init/${teamId}`,
    options,
  );
  return response.data.data;
};

export const createRecordingSession = async (
  liveUploadId: string,
  session: CreateSessionRequest,
) => {
  const response = await apiV2.post<IVFSResponse<SessionResponse>>(
    `/live/${liveUploadId}/sessions`,
    session,
  );
  return response.data.data;
};

export const initializeSessionUpload = async (
  liveUploadId: string,
  sessionId: string,
  options: InitUploadRequest,
) => {
  const response = await apiV2.post<IVFSResponse<InitUploadResponse>>(
    `/live/${liveUploadId}/sessions/${sessionId}/upload/init`,
    options,
  );
  return response.data.data;
};

export const getPartUploadUrl = async (
  liveUploadId: string,
  sessionId: string,
  partNumber: number,
  options: GetUploadUrlRequest,
) => {
  const response = await apiV2.put<IVFSResponse<GetUploadUrlResponse>>(
    `/live/${liveUploadId}/sessions/${sessionId}/upload/url/${partNumber}`,
    options,
  );
  return response.data.data.uploadUrl;
};

export const finalizeSessionUpload = async (
  liveUploadId: string,
  sessionId: string,
  options: FinalizeUploadRequest,
) => {
  try {
    logger.info('Finalizing session upload:', {
      liveUploadId,
      sessionId,
      options,
    });
    const response = await apiV2.put<IVFSResponse<FinalizeUploadResponse>>(
      `/live/${liveUploadId}/sessions/${sessionId}/upload/finalize`,
      options,
    );
    logger.info('Finalize session upload success:', response.data);
    return response.data.data;
  } catch (error: any) {
    logger.error('Finalize session upload error details:', {
      url: `/live/${liveUploadId}/sessions/${sessionId}/upload/finalize`,
      method: 'PUT',
      body: options,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      message: error.message,
    });
    throw error;
  }
};

export const updateTranscript = async (
  liveUploadId: string,
  sessionId: string,
  update: TranscriptUpdateRequest,
) => {
  await apiV2.put(
    `/live/${liveUploadId}/sessions/${sessionId}/transcript`,
    update,
  );
};

export const updateNotes = async (
  liveUploadId: string,
  sessionId: string,
  update: NotesUpdateRequest,
) => {
  await apiV2.put(`/live/${liveUploadId}/sessions/${sessionId}/notes`, update);
};

export const getLiveUploadStatus = async (liveUploadId: string) => {
  const response = await apiV2.get<IVFSResponse<any>>(`/live/${liveUploadId}`);
  return response.data.data;
};

export const finalizeSession = async (
  liveUploadId: string,
  sessionId: string,
  options: FinalizeSessionRequest,
) => {
  await apiV2.put(
    `/live/${liveUploadId}/sessions/${sessionId}/finalize`,
    options,
  );
};

export const finalizeLiveUpload = async (
  liveUploadId: string,
  options?: {
    name?: string;
    description?: string;
    cleanup?: boolean;
  },
) => {
  const response = await apiV2.post<
    IVFSResponse<{
      teamSpaceFileId: string;
      finalizedAt: string;
      mediaFile?: {
        providerKey: string;
        size: number;
        duration: number;
      };
      transcript?: {
        providerKey: string;
        segmentCount: number;
      };
      notes?: {
        noteCount: number;
      };
      cleanedUpSessions: string[];
      cleanedUpFiles: string[];
    }>
  >(`/live/${liveUploadId}/finalize`, options || {});
  return response.data.data;
};

export const discardLiveUpload = async (liveUploadId: string) => {
  await apiV2.delete(`/live/${liveUploadId}/discard`);
};

export const uploadChunkToS3 = async ({
  uploadUrl,
  chunk,
}: {
  uploadUrl: string;
  chunk: Blob;
}) => {
  if (!uploadUrl) {
    throw new Error('No upload URL provided');
  }
  try {
    await axios.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': chunk.type,
      },
    });
  } catch (error) {
    logger.error('Error uploading chunk to S3', error);
    throw error;
  }
};

/**
 * Get all live uploads for a team (optionally filter by finalized status)
 */
export const listLiveUploads = async (
  teamId: string,
  options?: { finalized?: boolean },
): Promise<InitializeLiveUploadResponse[]> => {
  const params = new URLSearchParams();
  if (options?.finalized !== undefined) {
    params.append('finalized', String(options.finalized));
  }

  const response = await apiV2.get<
    IVFSResponse<InitializeLiveUploadResponse[]>
  >(`/live/uploads/${teamId}?${params.toString()}`);
  return response.data.data;
};

/**
 * Get details of a specific live upload including session information
 */
export const getLiveUploadDetails = async (
  liveUploadId: string,
): Promise<{
  liveUpload: InitializeLiveUploadResponse;
  sessions: SessionResponse[];
}> => {
  const response = await apiV2.get<
    IVFSResponse<{
      liveUpload: InitializeLiveUploadResponse;
      sessions: SessionResponse[];
    }>
  >(`/live/${liveUploadId}/details`);
  return response.data.data;
};

/**
 * Get active live uploads for the current user
 */
export const getActiveLiveUploads = async (): Promise<
  Array<{
    liveUploadId: string;
    ownerId: string;
    teamId: string;
    folderId?: string;
    profile: string;
    status: 'active' | 'paused';
    totalRecordingTime?: number;
    sessionCount?: number;
    createdAt: string;
    updatedAt: string;
  }>
> => {
  const response = await apiV2.get<
    IVFSResponse<
      Array<{
        liveUploadId: string;
        ownerId: string;
        teamId: string;
        folderId?: string;
        profile: string;
        status: 'active' | 'paused';
        totalRecordingTime?: number;
        sessionCount?: number;
        createdAt: string;
        updatedAt: string;
      }>
    >
  >(`/live/active`);
  return response.data.data;
};
